import type { AnalyzeSpecSourceResult, SpecSection, SpecSource } from "../types/specs";
import type { ProjectDocument } from "../types/documents";

import { getEntitySlug } from "./entitySlug";

interface PaginatedResponse<T> {
  items: T[];
  meta: {
    skip: number;
    take: number;
    total: number;
  };
  error?: string;
}

interface CreateSpecSourceInput {
  projectId: string;
  name: string;
  sourceType: string;
  description?: string;
  uri?: string;
}

interface AnalyzeSpecSourceOptions {
  dryRun?: boolean;
}

interface UploadDocumentResponse {
  ok?: boolean;
  document?: ProjectDocument;
  error?: string;
  reason?: string;
}

function buildFiltersParam(filters: Record<string, unknown>): string {
  const payload = JSON.stringify(filters);
  return encodeURIComponent(payload);
}

function ensureListResponse<T>(payload: PaginatedResponse<T>): T[] {
  if (!payload || !Array.isArray(payload.items)) {
    throw new Error("Unexpected response while loading data");
  }

  return payload.items;
}

export async function fetchSpecSources(projectId: string): Promise<SpecSource[]> {
  const specSourceEntity = getEntitySlug("SpecSource");
  const filters = buildFiltersParam({ projectId });
  const response = await fetch(`/api/entities/${specSourceEntity}?page=0&pageSize=50&filters=${filters}`);

  if (!response.ok) {
    throw new Error(`Failed to load spec sources (status ${response.status})`);
  }

  const payload = (await response.json()) as PaginatedResponse<SpecSource>;

  return ensureListResponse(payload);
}

export async function createSpecSource(input: CreateSpecSourceInput): Promise<SpecSource> {
  const specSourceEntity = getEntitySlug("SpecSource");
  const response = await fetch(`/api/entities/${specSourceEntity}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = (await response.json()) as SpecSource & { error?: string };

  if (!response.ok) {
    throw new Error(payload.error ?? `Failed to create spec source (status ${response.status})`);
  }

  return payload;
}

export async function fetchSpecSections(projectId: string): Promise<SpecSection[]> {
  const specSectionEntity = getEntitySlug("SpecSection");
  const filters = buildFiltersParam({ projectId });
  const response = await fetch(`/api/entities/${specSectionEntity}?page=0&pageSize=200&filters=${filters}`);

  if (!response.ok) {
    throw new Error(`Failed to load spec sections (status ${response.status})`);
  }

  const payload = (await response.json()) as PaginatedResponse<SpecSection>;

  return ensureListResponse(payload);
}

export async function deleteSpecSection(specSectionId: string): Promise<void> {
  if (!specSectionId) {
    throw new Error("spec section id is required");
  }

  const response = await fetch(`/api/spec-sections/${encodeURIComponent(specSectionId)}`, {
    method: "DELETE",
  });

  const payload = ((await response.json().catch(() => ({}))) ?? {}) as { error?: string };

  if (!response.ok) {
    const message = payload.error ?? `Failed to delete spec section (status ${response.status})`;
    throw new Error(message);
  }
}

export async function analyzeSpecSource(
  specSourceId: string,
  options?: AnalyzeSpecSourceOptions,
): Promise<AnalyzeSpecSourceResult> {
  const response = await fetch(`/api/agent/spec-sources/${encodeURIComponent(specSourceId)}/analyze`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dryRun: options?.dryRun ?? false }),
  });

  const payload = (await response.json()) as AnalyzeSpecSourceResult & { error?: string };

  if (!response.ok) {
    throw new Error(payload.error ?? `Failed to analyze spec source (status ${response.status})`);
  }

  return payload;
}

export async function updateSpecSource(specSourceId: string, data: Partial<SpecSource>): Promise<SpecSource> {
  if (!specSourceId) {
    throw new Error("spec source id is required");
  }

  const specSourceEntity = getEntitySlug("SpecSource");
  const response = await fetch(`/api/entities/${specSourceEntity}/${encodeURIComponent(specSourceId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  const payload = (await response.json()) as SpecSource & { error?: string };

  if (!response.ok) {
    const message = payload.error ?? `Failed to update spec source (status ${response.status})`;
    throw new Error(message);
  }

  return payload;
}

export async function attachDocumentToSpecSource(
  specSourceId: string,
  documentId: string,
): Promise<SpecSource> {
  if (!documentId) {
    throw new Error("document id is required");
  }

  return updateSpecSource(specSourceId, { documentId });
}

export async function uploadSpecSourceDocument(
  projectId: string,
  specSourceId: string,
  file: File,
  options: { title?: string; signal?: AbortSignal } = {},
): Promise<{ document: ProjectDocument; specSource: SpecSource }> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  if (!specSourceId) {
    throw new Error("spec source id is required");
  }

  if (!file) {
    throw new Error("file is required");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("specSourceId", specSourceId);

  if (options.title) {
    formData.append("title", options.title);
  }

  const response = await fetch(`/api/projects/${projectId}/documents`, {
    method: "POST",
    body: formData,
    signal: options.signal,
  });

  const payload = (await response.json()) as UploadDocumentResponse;

  if (!response.ok || payload.ok === false || !payload.document) {
    const message =
      payload?.error ?? payload?.reason ?? `Failed to upload document (status ${response.status})`;
    throw new Error(message);
  }

  const specSource = await attachDocumentToSpecSource(specSourceId, payload.document.id);

  return { document: payload.document, specSource };
}
